import { useEffect, useMemo } from 'react';
import {
  Control,
  FieldErrors,
  Controller,
  UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { AddStandardFormData } from '@/schema/template';
import {
  useInfiniteDepartmentsQuery,
  useInfiniteTouchpointsQuery,
  useInfiniteSubTouchpointsQuery,
  useInfiniteTitlesQuery,
} from '@/hooks/queries/useTemplates';

interface HierarchyDropdownsProps {
  control: Control<AddStandardFormData>;
  errors: FieldErrors<AddStandardFormData>;
  setValue: UseFormSetValue<AddStandardFormData>;
  propertyTypeId: string | number;
  watchedDepartment: string;
  watchedTouchpoint: string;
}

export function HierarchyDropdowns({
  control,
  errors,
  setValue,
  propertyTypeId,
  watchedDepartment,
  watchedTouchpoint,
}: HierarchyDropdownsProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Fetch departments for this property type - fetch all items in single request
  const { data: departmentsData } = useInfiniteDepartmentsQuery(
    propertyTypeId,
    { page_size: 1000 }
  );
  const departments = departmentsData?.pages.flatMap(page => page.data) || [];

  // Fetch touchpoints for this property type
  const { data: touchpointsData } = useInfiniteTouchpointsQuery(
    propertyTypeId,
    { page_size: 1000 }
  );
  const touchpoints = touchpointsData?.pages.flatMap(page => page.data) || [];

  // Fetch sub-touchpoints for this property type
  const { data: subTouchpointsData } = useInfiniteSubTouchpointsQuery(
    propertyTypeId,
    { page_size: 1000 }
  );
  const subTouchpoints =
    subTouchpointsData?.pages.flatMap(page => page.data) || [];

  // Fetch titles for this property type
  const { data: titlesData } = useInfiniteTitlesQuery(propertyTypeId, { page_size: 1000 });
  const titles = titlesData?.pages.flatMap(page => page.data) || [];

  // Reset sub-touchpoint when touchpoint changes
  useEffect(() => {
    setValue('subtouchpoint', 'none');
  }, [watchedTouchpoint, setValue]);
  return (
    <div
      className={cn(
        'grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4',
        isRTL && 'rtl'
      )}
    >
      {/* Department Selection */}
      <div className="space-y-1 sm:space-y-2">
        <Label
          htmlFor="department"
          className={cn('text-xs sm:text-sm', isRTL && 'text-right')}
        >
          <span className="hidden sm:inline">
            {t('templates.admin.addStandards.form.department')}{' '}
            {t('templates.admin.addStandards.form.required')}
          </span>
          <span className="sm:hidden">Department *</span>
        </Label>
        <Controller
          name="department"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger
                className={cn(
                  'h-8 sm:h-10 text-xs sm:text-sm',
                  errors.department ? 'border-red-500' : '',
                  isRTL && 'text-right'
                )}
              >
                <SelectValue
                  placeholder={isRTL ? 'اختيار القسم' : 'Select Department'}
                />
              </SelectTrigger>
              <SelectContent>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={String(dept.id)}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.department && (
          <p className={cn('text-sm text-red-500', isRTL && 'text-right')}>
            {errors.department.message}
          </p>
        )}
      </div>

      {/* Touchpoint Selection */}
      <div className="space-y-1 sm:space-y-2 xs:col-span-2 lg:col-span-1">
        <Label
          htmlFor="touchpoint"
          className={cn('text-xs sm:text-sm', isRTL && 'text-right')}
        >
          <span className="hidden sm:inline">
            {t('templates.admin.addStandards.form.touchpoint')}{' '}
            {t('templates.admin.addStandards.form.required')}
          </span>
          <span className="sm:hidden">Touchpoint *</span>
        </Label>
        <Controller
          name="touchpoint"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value}
              disabled={!watchedDepartment}
            >
              <SelectTrigger
                className={cn(
                  'h-8 sm:h-10 text-xs sm:text-sm',
                  errors.touchpoint ? 'border-red-500' : '',
                  isRTL && 'text-right'
                )}
              >
                <SelectValue
                  placeholder={
                    isRTL ? 'اختيار نقطة الاتصال' : 'Select Touchpoint'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {touchpoints.map(tp => (
                  <SelectItem key={tp.id} value={String(tp.id)}>
                    {tp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.touchpoint && (
          <p className={cn('text-sm text-red-500', isRTL && 'text-right')}>
            {errors.touchpoint.message}
          </p>
        )}
      </div>

      {/* Sub-Touchpoint Selection */}
      <div className="space-y-1 sm:space-y-2 xs:col-span-2 lg:col-span-1">
        <Label
          htmlFor="subtouchpoint"
          className={cn('text-xs sm:text-sm', isRTL && 'text-right')}
        >
          <span className="hidden sm:inline">
            {t('templates.admin.addStandards.form.subTouchpoint')}
          </span>
          <span className="sm:hidden">Sub-Touchpoint</span>
        </Label>
        <Controller
          name="subtouchpoint"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value}
              disabled={!watchedTouchpoint}
            >
              <SelectTrigger
                className={cn(
                  'h-8 sm:h-10 text-xs sm:text-sm',
                  isRTL && 'text-right'
                )}
              >
                <SelectValue
                  placeholder={
                    isRTL ? 'اختيار نقطة فرعية' : 'Select Sub-Touchpoint'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  {t('templates.admin.addStandards.form.none')}
                </SelectItem>
                {subTouchpoints.map(subTp => (
                  <SelectItem key={subTp.id} value={String(subTp.id)}>
                    {subTp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Title Selection */}
      <div className="space-y-1 sm:space-y-2 xs:col-span-2 lg:col-span-1">
        <Label
          htmlFor="title"
          className={cn('text-xs sm:text-sm', isRTL && 'text-right')}
        >
          <span className="hidden sm:inline">
            {t('templates.admin.addStandards.form.title')}{' '}
            {t('templates.admin.addStandards.form.required')}
          </span>
          <span className="sm:hidden">Title *</span>
        </Label>
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value}
              disabled={!watchedTouchpoint}
            >
              <SelectTrigger
                className={cn(
                  'h-8 sm:h-10 text-xs sm:text-sm',
                  errors.title ? 'border-red-500' : '',
                  isRTL && 'text-right'
                )}
              >
                <SelectValue
                  placeholder={isRTL ? 'اختيار المسمى' : 'Select Title'}
                />
              </SelectTrigger>
              <SelectContent>
                {titles.map(title => (
                  <SelectItem key={title.id} value={String(title.id)}>
                    {title.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.title && (
          <p className={cn('text-sm text-red-500', isRTL && 'text-right')}>
            {errors.title.message}
          </p>
        )}
      </div>
    </div>
  );
}
